import React from 'react'
import { createContext, useContext, useLayoutEffect, useRef, useState } from 'react'

import type { StoreApi } from 'zustand'

import { createStore, useStore } from 'zustand'

type StoreProviderProps<P> = P & { children: React.ReactNode }
type StoreSet<S> = (stateOrFn: Partial<S> | ((state: S) => Partial<S>)) => void

interface Params<S, P> {
  defaultState: (props: P & { set: StoreSet<S> }) => S
  computed?: (state: S) => Partial<S>
  onStoreCreated?: (store: StoreApi<S>, props: P) => void
}

function hasShallowChanges(prev?: Record<string, unknown> | null, next?: Record<string, unknown> | null): boolean {
  if (!prev || !next) return true

  const prevKeys = Object.keys(prev)
  const nextKeys = Object.keys(next)

  if (prevKeys.length !== nextKeys.length) return true

  return nextKeys.some((key) => next[key] !== prev[key])
}

export function createStoreContext<S, P = unknown>({ defaultState, computed, onStoreCreated }: Params<S, P>) {
  const StoreContext = createContext<StoreApi<S> | null>(null)

  function StoreProvider({ children, ...props }: StoreProviderProps<P>) {
    const prevPropsRef = useRef<typeof props | null>(null)

    // Lazy init so the store is built once, without writing a ref during render.
    const [store] = useState(() => {
      const created = createStore<S>((set) => defaultState({ ...(props as P), set }))
      onStoreCreated?.(created, props as P)
      return created
    })

    useLayoutEffect(() => {
      const prev = prevPropsRef.current as Record<string, unknown> | null | undefined
      const next = props as Record<string, unknown> | null | undefined

      prevPropsRef.current = props

      if (!hasShallowChanges(prev, next)) return

      store.setState(props as unknown as Partial<S>)
      if (computed) {
        store.setState(computed(store.getState()))
      }
    })

    return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
  }

  function useStoreHook<T>(selector: (state: S) => T) {
    const store = useContext(StoreContext)
    if (!store) throw new Error('useStoreHook must be used within a Provider')
    return useStore(store, selector)
  }

  return {
    StoreProvider: React.memo(StoreProvider),
    useStore: useStoreHook,
  }
}
